import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'

import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Tooltip from '@mui/material/Tooltip'
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded'
import PaymentIcon from '@mui/icons-material/Payment'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'

import GroupsIcon from '@mui/icons-material/Groups'

import Popover from '@mui/material/Popover'
import Typography from '@mui/material/Typography'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'

import PatientIcon from '@/components/ui/icons/Patient'
import PdsIcon from '@/components/ui/icons/Pds'
import { PaymentType } from '@/core/models/consultation.model'
import { ReadinessChecklistSummary, WaitingRoomFairOffer } from '@/core/models/waiting-room.model'
import useInterval from '@/hooks/useInterval'
import { getCurrencySymbol } from '@/utils/currencies'

dayjs.extend(duration)

type WaitingConsultationData = {
  id: string
  isConsultable: boolean
  patient: string
  pds: string
  startDate: string
  paid?: boolean
  price?: number
  paymentType?: PaymentType
  isGroup?: boolean
  groupPatients?: string[]
  readiness?: 'ready' | 'preparing'
  checklist?: ReadinessChecklistSummary
}

type WaitingConsultationsTableProps = {
  data: WaitingConsultationData[]
  isAccepting?: boolean
  acceptingConsultationId?: string | null
  onAcceptConsultation?: (consultationId: string) => void
  readinessEnabled?: boolean
  acceptDisabledReason?: 'cooldown' | 'capReached'
  cooldownSecondsRemaining?: number
  // 'offer' ("Uber") mode: the head patient is reserved for one doctor at a time, rotating.
  offerMode?: boolean
  offer?: WaitingRoomFairOffer | null
  offerSecondsRemaining?: number
  // 'offer' mode: countdown until the round restarts after every online doctor passed
  nextRoundSecondsRemaining?: number
  isPassing?: boolean
  onPassOffer?: (consultationId: string) => void
  // SEHA (en-SA) read-only case preview; when omitted the table renders exactly as before
  onPreviewConsultation?: (consultationId: string) => void
}

export const WaitedTime = ({ startDate }: { startDate: string }) => {
  const [currentTime, setCurrentTime] = useState(dayjs())

  useInterval(() => {
    setCurrentTime(dayjs())
  }, 60 * 1000)

  const waitedTime = dayjs.duration(currentTime.diff(dayjs(startDate))).format("HH[h]mm[']")

  return (
    <Chip
      color="secondary"
      icon={
        <HourglassTopRoundedIcon
          sx={{
            animation: 'spin 6s ease-in-out infinite',
            '@keyframes spin': {
              '0%': {
                transform: 'rotate(360deg)',
              },
              '50%': {
                transform: 'rotate(180deg)',
              },
              '100%': {
                transform: 'rotate(0deg)',
              },
            },
          }}
        />
      }
      label={waitedTime}
      variant="filled"
    />
  )
}

/**
 * Readiness chip; when the room has a preparation checklist, "preparing" rows show progress
 * ("Preparing 3/5") and clicking opens a popover listing each item. Ready rows keep the plain
 * green chip (popover still available to see what was verified).
 */
function ReadinessChip({ readiness, checklist }: { readiness?: 'ready' | 'preparing'; checklist?: ReadinessChecklistSummary }) {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)

  const ready = readiness === 'ready'
  let chip: JSX.Element
  if (ready) {
    chip = (
      <Chip
        color="success"
        label={t('field.readiness.ready.label')}
        variant="filled"
        onClick={checklist ? (event) => setAnchorEl(event.currentTarget) : undefined}
        data-testid="readiness-checklist-chip"
      />
    )
  } else if (checklist?.hasNonBooleanFields) {
    // Non-boolean checklist: the signal is the submission STATUS, not a tick count.
    const submitted = checklist.status === 'submitted'
    const label = submitted
      ? t('field.readiness.checklist.prepared', { defaultValue: 'Prepared' })
      : checklist.status === 'draft'
      ? checklist.requiredTotal > 0
        ? t('field.readiness.checklist.preparingProgress', {
            defaultValue: 'Preparing {{answered}}/{{requiredTotal}}',
            answered: checklist.answered,
            requiredTotal: checklist.requiredTotal,
          })
        : t('field.readiness.checklist.preparing', { defaultValue: 'Preparing' })
      : t('field.readiness.checklist.notStarted', { defaultValue: 'Not started' })
    chip = (
      <Chip
        color={submitted ? 'success' : 'warning'}
        label={label}
        variant={submitted ? 'filled' : 'outlined'}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        data-testid="readiness-checklist-chip"
      />
    )
  } else if (checklist) {
    const submitted = checklist.status === 'submitted'
    chip = submitted ? (
      <Chip
        color="success"
        label={t('field.readiness.ready.label')}
        variant="filled"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        data-testid="readiness-checklist-chip"
      />
    ) : (
      <Chip
        color="warning"
        label={t('field.readiness.preparing.progress', { completed: checklist.completed, total: checklist.total })}
        variant="outlined"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        data-testid="readiness-checklist-chip"
      />
    )
  } else {
    chip = <Chip color="warning" label={t('field.readiness.preparing.label')} variant="outlined" />
  }

  return (
    <>
      {chip}
      {checklist && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Stack spacing={1} sx={{ p: 2, minWidth: 240 }} data-testid="readiness-checklist-chip-popover">
            <Typography variant="subtitle2">{checklist.templateTitle || t('page.waitingRoom.checklist.popover.title')}</Typography>
            {checklist.items.map((item) => (
              <Stack key={item.fieldId} direction="row" alignItems="center" spacing={1}>
                {item.done ? <CheckCircleOutlineIcon color="success" fontSize="small" /> : <RadioButtonUncheckedIcon color="disabled" fontSize="small" />}
                <Typography variant="body2">{item.label}</Typography>
              </Stack>
            ))}
          </Stack>
        </Popover>
      )}
    </>
  )
}

const columnHelper = createColumnHelper<WaitingConsultationData>()

export function WaitingConsultationsTable(props: WaitingConsultationsTableProps) {
  const {
    data,
    isAccepting = false,
    acceptingConsultationId,
    onAcceptConsultation,
    readinessEnabled = false,
    acceptDisabledReason,
    cooldownSecondsRemaining = 0,
    offerMode = false,
    offer = null,
    offerSecondsRemaining = 0,
    nextRoundSecondsRemaining = 0,
    isPassing = false,
    onPassOffer,
    onPreviewConsultation,
  } = props
  const { t, i18n } = useTranslation()

  const language = i18n.language

  const columns = useMemo(
    () => [
      columnHelper.accessor('patient', {
        cell: ({ getValue, row }) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            {row.original.isGroup ? <GroupsIcon color="primary" /> : <PatientIcon />}
            <span>{getValue()}</span>
            {row.original.isGroup && row.original.groupPatients && (
              <Chip size="small" label={`${row.original.groupPatients.length}`} color="primary" variant="outlined" />
            )}
          </Stack>
        ),
      }),
      ...(readinessEnabled
        ? [
            columnHelper.accessor('readiness', {
              cell: ({ getValue, row }) => <ReadinessChip readiness={getValue()} checklist={row.original.checklist} />,
            }),
          ]
        : []),
      columnHelper.accessor('pds', {
        cell: ({ getValue }) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            <PdsIcon />
            <span>{getValue()}</span>
          </Stack>
        ),
      }),
      columnHelper.accessor('startDate', {
        cell: ({ getValue }) => <WaitedTime startDate={getValue()} />,
      }),
      columnHelper.accessor('paid', {
        cell: ({ getValue }) => <Chip color={getValue() ? 'success' : 'error'} label={t(`field.paid.${getValue()}.label`)} variant="filled" />,
      }),
      columnHelper.accessor('price', {
        cell: ({ getValue }) => {
          const currencySymbol = getCurrencySymbol(language)
          const price = getValue() ?? 0
          return <Chip color="info" label={`${price} ${currencySymbol}`} variant="filled" />
        },
      }),
      columnHelper.accessor('paymentType', {
        cell: ({ getValue }) => {
          const paymentType = getValue()
          return (
            // TODO: replace react-i18next by lingui
            <Chip color="primary" icon={<PaymentIcon />} label={paymentType ? t(`page.liveConsultation.payment.type.${paymentType.toLowerCase()}`) : 'N/A'} />
          )
        },
      }),
    ],
    [language, t, readinessEnabled]
  )

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <TableContainer>
      <Table sx={{ borderCollapse: 'separate', borderSpacing: '0 15px', minWidth: 500, padding: '0 5px' }}>
        <TableBody>
          {table.getRowModel().rows.map((row) => {
            const isLoading = isAccepting && acceptingConsultationId === row.original.id
            const isDisabled = isAccepting && acceptingConsultationId !== row.original.id
            // Fair-distribution gate (doctor-scoped, same for every row). Empty title => no tooltip.
            const fairDisabledTitle =
              acceptDisabledReason === 'cooldown'
                ? t('page.waitingRoom.accept.cooldown', { seconds: cooldownSecondsRemaining })
                : acceptDisabledReason === 'capReached'
                ? t('page.waitingRoom.accept.capReached')
                : ''
            return (
              <Paper key={row.original.id || row.id} component={TableRow}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={`${row.original.id}-${cell.id}`}
                    sx={{
                      borderBottom: 'none',
                      '&:first-of-type': {
                        borderTopLeftRadius: '6px',
                        borderBottomLeftRadius: '6px',
                      },
                      '&:last-of-type': {
                        borderTopRightRadius: '6px',
                        borderBottomRightRadius: '6px',
                      },
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
                <TableCell
                  key={`${row.original.id}-actions`}
                  align="right"
                  sx={{ borderTopRightRadius: '6px', borderBottomRightRadius: '6px', borderBottom: 'none' }}
                >
                  {onPreviewConsultation && (
                    <Tooltip title={t('page.waitingRoom.preview.button')}>
                      <IconButton
                        size="small"
                        onClick={() => onPreviewConsultation(row.original.id)}
                        sx={{ mr: 1 }}
                        data-testid="waiting-room-preview-consultation"
                      >
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {offerMode ? (
                    // 'offer' mode: the offered doctor gets Accept + Pass on their reserved row; everyone
                    // else sees that the reserved row is held by another doctor. Other rows show nothing.
                    !offer && nextRoundSecondsRemaining > 0 && row.index === 0 ? (
                      // everyone online passed: the head patient re-enters rotation when the pause ends
                      <Chip color="warning" size="small" variant="outlined" label={t('page.waitingRoom.offer.nextRound', { seconds: nextRoundSecondsRemaining })} />
                    ) : offer && offer.consultationId === row.original.id ? (
                      offer.isMyTurn ? (
                        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
                          <Chip color="success" size="small" label={t('page.waitingRoom.offer.yourTurn', { seconds: offerSecondsRemaining })} />
                          <LoadingButton
                            loading={isLoading}
                            disabled={isDisabled}
                            onClick={() => onAcceptConsultation?.(row.original.id)}
                            size="small"
                            variant="contained"
                            data-testid="waiting-room-accept-consultation"
                          >
                            {t('common:button.accept.label')}
                          </LoadingButton>
                          <Button
                            onClick={() => onPassOffer?.(row.original.id)}
                            disabled={isPassing}
                            size="small"
                            variant="neutral"
                            data-testid="waiting-room-pass-offer"
                          >
                            {t('page.waitingRoom.offer.pass')}
                          </Button>
                        </Stack>
                      ) : (
                        <Chip color="info" size="small" variant="outlined" label={t('page.waitingRoom.offer.offeredToOther', { seconds: offerSecondsRemaining })} />
                      )
                    ) : null
                  ) : (
                    row.original.isConsultable && (
                      <Tooltip title={fairDisabledTitle}>
                        <span>
                          <LoadingButton
                            loading={isLoading}
                            disabled={isDisabled || !!acceptDisabledReason}
                            onClick={() => onAcceptConsultation?.(row.original.id)}
                            size="small"
                            variant="contained"
                            data-testid="waiting-room-accept-consultation"
                          >
                            {t('common:button.accept.label')}
                          </LoadingButton>
                        </span>
                      </Tooltip>
                    )
                  )}
                </TableCell>
              </Paper>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
